import { useState } from "react";
import {
  User,
  Mail,
  Edit2,
  Save,
  X,
  Eye,
  EyeOff,
  Lock,
  CheckCircle,
  Shield,
  Bell,
  Award,
  MessageCircle,
  Smartphone,
  AlertCircle,
} from "lucide-react";
import BaseApi from "../services/base-api";
import { useToast } from "../context/ToastContext";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

const MyProfile = () => {
  const normalizePhoneCode = (code?: string) => {
    if (!code) return "";
    return code.startsWith("+") ? code.slice(1) : code;
  };

  const normalizeProfile = (data: any) => ({
    ...data,
    phoneCode: normalizePhoneCode(data?.phoneCode),
    preferences: {
      language: data?.preferences?.language ?? "en",
      notifications: {
        email: data?.preferences?.notifications?.email ?? true,
        whatsapp: data?.preferences?.notifications?.whatsapp ?? true,
      },
    },
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { success, err } = useToast();
  const { t } = useTranslation();
  const { updateUser } = useAuth();
  const [profileData, setProfileData] = useState(() => {
    const storedUser = localStorage.getItem("userData");
    if (storedUser) return normalizeProfile(JSON.parse(storedUser));

    return normalizeProfile({
      firstName: "",
      lastName: "",
      email: "",
      phoneCode: "",
      phone: "",
    });
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleInputChange = (field: string, value: string | boolean) => {
    if (field.startsWith("preferences.notifications.")) {
      const notifType = field.split(".").pop() as "email" | "whatsapp";

      setProfileData((prev: any) => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          notifications: {
            ...prev.preferences.notifications,
            [notifType]: value as boolean,
          },
        },
      }));
      return;
    }
    setProfileData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await BaseApi.put("/users/profile", {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phoneCode: profileData.phoneCode,
        phone: profileData.phone,
        preferences: profileData.preferences,
      });

      if (res.success && res?.data) {
        const normalized = normalizeProfile(res.data);
        updateUser(normalized);
        localStorage.setItem("userData", JSON.stringify(normalized));
        setProfileData(normalized);
        setIsSaving(false);
        setIsEditing(false);
        success(t("Your profile has been updated"));
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      setIsSaving(false);
      console.error(t("Failed to update profile. Please try again."), error);
    }
  };

  const handleCancel = () => {
    const storedUser = localStorage.getItem("userData");
    if (storedUser) {
      setProfileData(normalizeProfile(JSON.parse(storedUser)));
    }
    setIsEditing(false);
  };

  const validatePasswordChange = () => {
    const errors = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    };

    if (!passwordData.currentPassword) {
      errors.currentPassword = t("Current password is required");
    }

    if (!passwordData.newPassword) {
      errors.newPassword = t("New password is required");
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = t("Password must be at least 8 characters");
    } else if (
      !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.newPassword)
    ) {
      errors.newPassword = t(
        "Password must contain uppercase, lowercase, and number",
      );
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = t("Please confirm your new password");
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = t("Passwords do not match");
    }

    setPasswordErrors(errors);
    return (
      !errors.currentPassword && !errors.newPassword && !errors.confirmPassword
    );
  };

  const handlePasswordChange = async () => {
    if (validatePasswordChange()) {
      setIsSaving(true);
      const res: any = await BaseApi.post("/auth/change-password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      if (res && res?.success) {
        success(t("Your Password has been changed successfully!"));
      } else {
        err(
          res.message || t("Failed to change your password. Please try again."),
        );
      }
      setIsSaving(false);
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordErrors({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordErrors({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  // Password strength indicator
  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, label: "", color: "" };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2)
      return { strength: 33, label: t("Weak"), color: "bg-red-500" };
    if (strength <= 3)
      return { strength: 66, label: t("Good"), color: "bg-yellow-500" };
    return { strength: 100, label: t("Strong"), color: "bg-green-500" };
  };

  const passwordStrength = getPasswordStrength(passwordData.newPassword);

  const phoneCodes = [
    { code: "+971", country: "AE", name: "United Arab Emirates", flag: "🇦🇪" },
    { code: "+968", country: "OM", name: "Oman", flag: "🇴🇲" },
    { code: "+973", country: "BH", name: "Bahrain", flag: "🇧🇭" },
    { code: "+965", country: "KW", name: "Kuwait", flag: "🇰🇼" },
    { code: "+966", country: "SA", name: "Saudi Arabia", flag: "🇸🇦" },
    { code: "+974", country: "QA", name: "Qatar", flag: "🇶🇦" },
    { code: "+91", country: "IN", name: "India", flag: "🇮🇳" },
  ];

  return (
    <div className="min-h-screen bg-[#f2e6db] py-8 px-4">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .input-focus-effect {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .input-focus-effect:focus {
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }
        .hover-lift {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .hover-lift:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
        }
      `}</style>

      <div className="max-w-5xl mx-auto">
        {/* Enhanced Header with Avatar */}
        <div
          className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-6"
          style={{ animation: "fadeIn 0.6s ease-out" }}
        >
          {/* Decorative Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, black 1px, transparent 0)`,
                backgroundSize: "40px 40px",
              }}
            />
          </div>

          <div className="relative p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex items-center gap-6 w-full sm:w-auto">
                {/* Avatar with Status Indicator */}
                <div className="relative">
                  <div className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-2xl p-5 shadow-lg hover-lift">
                    <User className="w-12 h-12 sm:w-14 sm:h-14 text-white" />
                  </div>
                  <div
                    className={`absolute -bottom-1 -right-1 bg-green-600 w-5 h-5 rounded-full border-4 border-black`}
                    style={{ animation: "pulse 2s ease-in-out infinite" }}
                  />
                </div>

                {/* User Info */}
                <div className="flex-1">
                  <h1 className="text-3xl sm:text-4xl font-bold text-black mb-2 tracking-tight">
                    {profileData.firstName} {profileData.lastName}
                  </h1>
                </div>
              </div>

              {/* Action Buttons */}
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="w-full border border-black sm:w-auto flex items-center justify-center gap-2 bg-white text-black px-6 py-3.5 rounded-xl hover:bg-gray-200 transition-all duration-300 font-semibold shadow-lg hover-lift"
                >
                  <Edit2 className="w-5 h-5" />
                  <span>{t("Edit Profile")}</span>
                </button>
              ) : (
                <div className="flex gap-3 w-full sm:w-auto">
                  <button
                    onClick={handleCancel}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-gray-800 text-white px-6 py-3.5 rounded-xl hover:bg-gray-700 transition-all duration-300 font-semibold"
                  >
                    <X className="w-5 h-5" />
                    <span>{t("Cancel")}</span>
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 border border-black sm:flex-none flex items-center justify-center gap-2 bg-white text-black px-6 py-3.5 rounded-xl hover:bg-gray-100 transition-all duration-300 font-semibold disabled:opacity-70 disabled:cursor-not-allowed shadow-lg hover-lift"
                  >
                    {isSaving ? (
                      <>
                        <div
                          className="w-5 h-5 border-2 border-black border-t-transparent rounded-full"
                          style={{ animation: "spin 1s linear infinite" }}
                        />
                        <span>{t("Saving...")}</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        <span>{t("Save")}</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Form - Takes 2 columns */}
          <div
            className={`lg:col-span-2 rounded-3xl shadow-xl overflow-hidden ${
              !isEditing ? "bg-white" : "bg-gray-50"
            }`}
            style={{ animation: "slideUp 0.6s ease-out 0.1s backwards" }}
          >
            <div className="p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="bg-black rounded-lg p-2">
                  <User className="w-5 h-5 text-white" />
                </div>
                {t("Personal Information")}
              </h2>

              <div className="space-y-6">
                {/* Name Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      {t("First Name")}
                    </label>
                    <div className="relative group">
                      <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-focus-within:text-black" />
                      <input
                        type="text"
                        value={profileData.firstName}
                        onChange={(e) =>
                          handleInputChange("firstName", e.target.value)
                        }
                        disabled={!isEditing}
                        className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-black focus:outline-none transition-all duration-300 font-medium input-focus-effect ${
                          !isEditing
                            ? "bg-gray-50 cursor-not-allowed text-gray-600"
                            : "bg-white"
                        }`}
                        placeholder={t("Enter first name")}
                      />
                    </div>
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      {t("Last Name")}
                    </label>
                    <div className="relative group">
                      <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-focus-within:text-black" />
                      <input
                        type="text"
                        value={profileData.lastName}
                        onChange={(e) =>
                          handleInputChange("lastName", e.target.value)
                        }
                        disabled={!isEditing}
                        className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-black focus:outline-none transition-all duration-300 font-medium input-focus-effect ${
                          !isEditing
                            ? "bg-gray-50 cursor-not-allowed text-gray-600"
                            : "bg-white"
                        }`}
                        placeholder={t("Enter last name")}
                      />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center justify-between">
                    <span>{t("Email Address")}</span>
                  </label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-focus-within:text-black" />
                    <input
                      type="email"
                      value={profileData.email || ""}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      disabled={!isEditing}
                      className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-black focus:outline-none transition-all duration-300 font-medium input-focus-effect ${
                        !isEditing
                          ? "bg-gray-50 cursor-not-allowed text-gray-600"
                          : "bg-white"
                      }`}
                      placeholder={t("Enter email")}
                    />
                  </div>
                  {!profileData.email && (
                    <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {t("No email address added")}
                    </p>
                  )}
                </div>

                {/* Phone with Country Code */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center justify-between">
                    <span>{t("Phone Number")}</span>
                  </label>
                  <div className="flex gap-3">
                    {/* Country Code Selector */}
                    <div className="relative group w-32">
                      <select
                        value={
                          profileData.phoneCode
                            ? `+${profileData.phoneCode.replace(/^\+/, "")}`
                            : ""
                        }
                        onChange={(e) =>
                          handleInputChange(
                            "phoneCode",
                            e.target.value.replace(/^\+/, ""),
                          )
                        }
                        disabled={!isEditing}
                        className={`w-full pl-2 py-5 rounded-xl border-2 border-gray-200 focus:border-black focus:outline-none transition-all duration-300 font-medium text-sm ${
                          !isEditing
                            ? "bg-gray-50 cursor-not-allowed text-gray-600"
                            : "bg-white"
                        }`}
                      >
                        {phoneCodes.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.flag} {item.code}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Phone Number */}
                    <div className="relative group flex-1">
                      <Smartphone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-focus-within:text-black" />
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        disabled={!isEditing}
                        className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-200 focus:border-black focus:outline-none transition-all duration-300 font-medium input-focus-effect ${
                          !isEditing
                            ? "bg-gray-50 cursor-not-allowed text-gray-600"
                            : "bg-white"
                        }`}
                        placeholder={t("Enter phone number")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Security Section: Hiding currently */}
            {false && (
              <div
                className="bg-gradient-to-br from-gray-900 to-black rounded-3xl shadow-xl overflow-hidden hover-lift"
                style={{ animation: "slideUp 0.6s ease-out 0.2s backwards" }}
              >
                <div className="p-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    {t("Security")}
                  </h3>
                  <button
                    onClick={() => setShowPasswordModal(true)}
                    disabled={!profileData.email}
                    className={`w-full bg-white text-black px-5 py-4 rounded-xl transition-all duration-300 font-semibold flex items-center justify-center gap-2 shadow-lg ${
                      profileData.email
                        ? "hover:bg-gray-100 cursor-pointer"
                        : "opacity-50 cursor-not-allowed"
                    }`}
                  >
                    <Shield className="w-5 h-5" />
                    <span>{t("Change Password")}</span>
                  </button>
                  {!profileData.email && (
                    <p className="text-yellow-300 text-xs mt-3 text-center flex items-center justify-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {t("Add email to set password")}
                    </p>
                  )}
                  <p className="text-gray-400 text-xs mt-3 text-center">
                    {t("Last login")}:{" "}
                    {new Date(
                      profileData.lastLogin || Date.now(),
                    ).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}

            {/* Notification Preferences */}
            <div
              className="bg-white rounded-3xl shadow-xl overflow-hidden hover-lift"
              style={{ animation: "slideUp 0.6s ease-out 0.3s backwards" }}
            >
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  {t("Notifications")}
                </h3>
                <div className="space-y-3">
                  {/* Email Notifications */}
                  <label
                    className={`flex items-start gap-4 p-4 rounded-xl bg-gray-50 transition-all duration-300 group ${
                      isEditing
                        ? "cursor-pointer hover:bg-gray-100"
                        : "cursor-not-allowed pointer-events-none opacity-70"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={
                        profileData.preferences?.notifications?.email || false
                      }
                      onChange={(e) =>
                        handleInputChange(
                          "preferences.notifications.email",
                          e.target.checked,
                        )
                      }
                      disabled={!isEditing}
                      className={`w-5 h-5 mt-0.5 rounded border-gray-300 text-black focus:ring-black focus:ring-2 ${
                        isEditing
                          ? "cursor-pointer"
                          : "cursor-not-allowed opacity-60"
                      }`}
                    />
                    <div className="flex-1">
                      <span className="text-sm font-semibold text-gray-900 block mb-1 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {t("Email")}
                      </span>
                      <p className="text-xs text-gray-600">
                        {t("Order updates via email")}
                      </p>
                    </div>
                  </label>

                  {/* WhatsApp Notifications */}
                  <label
                    className={`flex items-start gap-4 p-4 rounded-xl bg-gray-50 transition-all duration-300 group ${
                      isEditing
                        ? "cursor-pointer hover:bg-gray-100"
                        : "cursor-not-allowed pointer-events-none opacity-70"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={
                        profileData.preferences?.notifications?.whatsapp ||
                        false
                      }
                      onChange={(e) =>
                        handleInputChange(
                          "preferences.notifications.whatsapp",
                          e.target.checked,
                        )
                      }
                      disabled={!isEditing}
                      className={`w-5 h-5 mt-0.5 rounded border-gray-300 text-black focus:ring-black focus:ring-2 ${
                        isEditing
                          ? "cursor-pointer"
                          : "cursor-not-allowed opacity-60"
                      }`}
                    />
                    <div className="flex-1">
                      <span className="text-sm font-semibold text-gray-900 block mb-1 flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" />
                        {t("WhatsApp")}
                      </span>
                      <p className="text-xs text-gray-600">
                        {t("Updates on WhatsApp")}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Account Stats */}
            <div
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl shadow-xl overflow-hidden"
              style={{ animation: "slideUp 0.6s ease-out 0.4s backwards" }}
            >
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  {t("Account Status")}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      {t("Member Since")}
                    </span>
                    <span className="text-sm font-bold text-gray-900">
                      {new Date(
                        profileData.createdAt || Date.now(),
                      ).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      {t("Account Type")}
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-black text-white text-xs font-bold capitalize">
                      {t(profileData.role || "Customer")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closePasswordModal}
          style={{ animation: "fadeIn 0.3s ease-out" }}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: "slideDown 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-black via-gray-700 to-black px-6 py-5 flex items-center justify-between sticky top-0 z-10">
              <h3 className="text-xl font-bold text-white flex items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <Lock className="w-6 h-6" />
                </div>
                {t("Change Password")}
              </h3>
              <button
                onClick={closePasswordModal}
                className="text-white hover:text-gray-300 transition-colors p-2 hover:bg-white hover:bg-opacity-10 rounded-lg"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  {t("Current Password")}
                </label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-focus-within:text-black" />
                  <input
                    type={showPasswords.old ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) => {
                      setPasswordData({
                        ...passwordData,
                        currentPassword: e.target.value,
                      });
                      if (passwordErrors.currentPassword) {
                        setPasswordErrors({
                          ...passwordErrors,
                          currentPassword: "",
                        });
                      }
                    }}
                    className={`w-full pl-12 pr-12 py-4 rounded-xl border-2 ${
                      passwordErrors.currentPassword
                        ? "border-red-500 focus:border-red-500 bg-red-50"
                        : "border-gray-200 focus:border-black"
                    } focus:outline-none transition-all duration-300 font-medium input-focus-effect`}
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswords({
                        ...showPasswords,
                        old: !showPasswords.old,
                      })
                    }
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-all"
                  >
                    {showPasswords.old ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {passwordErrors.currentPassword && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                    {passwordErrors.currentPassword}
                  </p>
                )}
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  {t("New Password")}
                </label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-focus-within:text-black" />
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => {
                      setPasswordData({
                        ...passwordData,
                        newPassword: e.target.value,
                      });
                      if (passwordErrors.newPassword) {
                        setPasswordErrors({
                          ...passwordErrors,
                          newPassword: "",
                        });
                      }
                    }}
                    className={`w-full pl-12 pr-12 py-4 rounded-xl border-2 ${
                      passwordErrors.newPassword
                        ? "border-red-500 focus:border-red-500 bg-red-50"
                        : "border-gray-200 focus:border-black"
                    } focus:outline-none transition-all duration-300 font-medium input-focus-effect`}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswords({
                        ...showPasswords,
                        new: !showPasswords.new,
                      })
                    }
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-all"
                  >
                    {showPasswords.new ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {passwordData.newPassword && (
                  <div className="mt-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-semibold text-gray-600">
                        {t("Password Strength")}
                      </span>
                      <span
                        className={`text-xs font-bold ${
                          passwordStrength.strength === 100
                            ? "text-green-600"
                            : passwordStrength.strength === 66
                              ? "text-yellow-600"
                              : "text-red-600"
                        }`}
                      >
                        {passwordStrength.label}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${passwordStrength.color} transition-all duration-500 rounded-full`}
                        style={{ width: `${passwordStrength.strength}%` }}
                      />
                    </div>
                  </div>
                )}

                {passwordErrors.newPassword && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                    {passwordErrors.newPassword}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  {t("Confirm New Password")}
                </label>
                <div className="relative group">
                  <CheckCircle className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-focus-within:text-black" />
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) => {
                      setPasswordData({
                        ...passwordData,
                        confirmPassword: e.target.value,
                      });
                      if (passwordErrors.confirmPassword) {
                        setPasswordErrors({
                          ...passwordErrors,
                          confirmPassword: "",
                        });
                      }
                    }}
                    className={`w-full pl-12 pr-12 py-4 rounded-xl border-2 ${
                      passwordErrors.confirmPassword
                        ? "border-red-500 focus:border-red-500 bg-red-50"
                        : "border-gray-200 focus:border-black"
                    } focus:outline-none transition-all duration-300 font-medium input-focus-effect`}
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswords({
                        ...showPasswords,
                        confirm: !showPasswords.confirm,
                      })
                    }
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-all"
                  >
                    {showPasswords.confirm ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {passwordErrors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                    {passwordErrors.confirmPassword}
                  </p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-5 border-2 border-gray-200">
                <p className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  {t("Password Requirements")}
                </p>
                <ul className="space-y-2">
                  <li className="text-xs text-gray-700 flex items-center gap-2">
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${
                        passwordData.newPassword.length >= 8
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                    />
                    {t("At least 8 characters")}
                  </li>
                  <li className="text-xs text-gray-700 flex items-center gap-2">
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${
                        /[A-Z]/.test(passwordData.newPassword)
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                    />
                    {t("One uppercase letter")}
                  </li>
                  <li className="text-xs text-gray-700 flex items-center gap-2">
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${
                        /[a-z]/.test(passwordData.newPassword)
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                    />
                    {t("One lowercase letter")}
                  </li>
                  <li className="text-xs text-gray-700 flex items-center gap-2">
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${
                        /\d/.test(passwordData.newPassword)
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                    />
                    {t("One number")}
                  </li>
                </ul>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={closePasswordModal}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-4 rounded-xl hover:bg-gray-300 transition-all duration-300 font-bold"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handlePasswordChange}
                disabled={isSaving}
                className="flex-1 bg-gradient-to-r from-black to-gray-700 text-white px-6 py-4 rounded-xl hover:from-gray-900 hover:to-black transition-all duration-300 font-bold disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover-lift"
              >
                {isSaving ? (
                  <>
                    <div
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    <span>{t("Updating...")}</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    {t("Update Password")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProfile;
